/**
 * kw-elevate.js — site-wide "elevate" layer for Kindred Wooden.
 * - Scroll reveal (fade + rise) via IntersectionObserver
 * - Image parallax on hover for editorial blocks
 * - Cursor blob follow (desktop only)
 * - Sticky header micro-shadow on scroll
 * - Animated number counter (data-kw-count)
 * - Split-word entrance on [data-kw-split]
 * - Marquee auto-scroll for [data-kw-marquee]
 */
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isTouch = 'ontouchstart' in window && navigator.maxTouchPoints > 0;
  var scrollRoot = document.querySelector('.page-wrapper') || null;

  // ------------------------------------------------------------
  // Auto-tag elements for scroll reveal on public sections
  // Consumers can also opt in explicitly with class="kw-reveal"
  // or attribute data-kw-reveal
  // ------------------------------------------------------------
  function autoTagReveals() {
    var selectors = [
      'section h2', 'section h3',
      '.rte p', '.rte li', '.rte h2', '.rte h3', '.rte h4', '.rte img',
      '[data-kw-reveal]', '.kw-reveal',
      '.kw-editorial__block'
    ];
    document.querySelectorAll(selectors.join(',')).forEach(function (el) {
      if (el.classList.contains('kw-reveal-init')) return;
      el.classList.add('kw-reveal-init');
    });
  }

  function initReveal() {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      document.querySelectorAll('.kw-reveal-init').forEach(function (el) {
        el.classList.add('kw-reveal-in');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('kw-reveal-in');
          io.unobserve(entry.target);
        }
      });
    }, { root: scrollRoot, rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    document.querySelectorAll('.kw-reveal-init:not(.kw-reveal-in)').forEach(function (el) {
      io.observe(el);
    });
  }

  // ------------------------------------------------------------
  // Split-word entrance
  // Turn [data-kw-split] into words with staggered rise
  // ------------------------------------------------------------
  function initSplit() {
    document.querySelectorAll('[data-kw-split]:not([data-kw-split-done])').forEach(function (el) {
      el.setAttribute('data-kw-split-done', '1');
      var text = el.textContent.trim();
      var words = text.split(/\s+/);
      el.innerHTML = words.map(function (w, i) {
        return '<span class="kw-split-word" style="--kw-i:' + i + '"><span class="kw-split-inner">' + w + '</span></span>';
      }).join(' ');
      requestAnimationFrame(function () { el.classList.add('kw-split-in'); });
    });
  }

  // ------------------------------------------------------------
  // Parallax hover for editorial media blocks
  // Any [data-kw-parallax] container listens to mousemove
  // ------------------------------------------------------------
  function initParallax() {
    if (isTouch || reduceMotion) return;
    document.querySelectorAll('[data-kw-parallax]:not([data-kw-parallax-init])').forEach(function (el) {
      el.setAttribute('data-kw-parallax-init', '1');
      var media = el.querySelector('img, video, .kw-parallax-target') || el;
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        media.style.transform = 'translate3d(' + (-px * 12) + 'px,' + (-py * 12) + 'px,0) scale(1.04)';
      });
      el.addEventListener('mouseleave', function () {
        media.style.transform = 'translate3d(0,0,0) scale(1)';
      });
    });
  }

  // ------------------------------------------------------------
  // Cursor blob (desktop-only, subtle)
  // ------------------------------------------------------------
  function initCursor() {
    if (isTouch || reduceMotion) return;
    if (document.getElementById('kw-cursor')) return;
    var blob = document.createElement('div');
    blob.id = 'kw-cursor';
    document.body.appendChild(blob);
    var x = window.innerWidth / 2, y = window.innerHeight / 2;
    var tx = x, ty = y;
    window.addEventListener('mousemove', function (e) { tx = e.clientX; ty = e.clientY; });
    (function loop() {
      x += (tx - x) * 0.15; y += (ty - y) * 0.15;
      blob.style.transform = 'translate3d(' + (x - 12) + 'px,' + (y - 12) + 'px,0)';
      requestAnimationFrame(loop);
    })();
    // Enlarge on interactive
    var interactive = 'a, button, [role=button], input, .kw-fav-card-btn, product-card, .kw-editorial__block';
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest && e.target.closest(interactive)) blob.classList.add('kw-cursor--big');
    });
    document.addEventListener('mouseout', function (e) {
      if (e.target.closest && e.target.closest(interactive)) blob.classList.remove('kw-cursor--big');
    });
  }

  // ------------------------------------------------------------
  // Header shadow / condense on scroll
  // ------------------------------------------------------------
  function initHeaderScroll() {
    var header = document.getElementById('header-component') || document.querySelector('header');
    if (!header) return;
    var scroller = scrollRoot || window;
    var lastY = 0;
    function onScroll() {
      var y = scroller === window ? window.scrollY : scroller.scrollTop;
      if (y > 40) header.classList.add('kw-header-scrolled');
      else header.classList.remove('kw-header-scrolled');
      lastY = y;
    }
    scroller.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ------------------------------------------------------------
  // Number counter on visibility (data-kw-count="1200")
  // ------------------------------------------------------------
  function initCounters() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var target = parseInt(el.getAttribute('data-kw-count'), 10) || 0;
        var dur = parseInt(el.getAttribute('data-kw-count-duration'), 10) || 1400;
        var start = performance.now();
        function step(now) {
          var t = Math.min(1, (now - start) / dur);
          var eased = 1 - Math.pow(1 - t, 3);
          el.textContent = Math.round(target * eased).toLocaleString();
          if (t < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
        io.unobserve(el);
      });
    }, { threshold: 0.4 });
    document.querySelectorAll('[data-kw-count]:not([data-kw-count-done])').forEach(function (el) {
      el.setAttribute('data-kw-count-done', '1');
      io.observe(el);
    });
  }

  // ------------------------------------------------------------
  // Marquee: seamless auto-scroll clone
  // ------------------------------------------------------------
  function initMarquee() {
    if (reduceMotion) return;
    document.querySelectorAll('[data-kw-marquee]:not([data-kw-marquee-init])').forEach(function (el) {
      el.setAttribute('data-kw-marquee-init', '1');
      var track = el.firstElementChild;
      if (!track) return;
      track.classList.add('kw-marquee-track');
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      el.appendChild(clone);
    });
  }

  // ------------------------------------------------------------
  function init() {
    autoTagReveals();
    initReveal();
    initSplit();
    initParallax();
    initCursor();
    initHeaderScroll();
    initCounters();
    initMarquee();

    if (window.MutationObserver) {
      var mo = new MutationObserver(function () {
        autoTagReveals();
        initReveal();
        initSplit();
        initParallax();
        initCounters();
        initMarquee();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
